import type { ExperimentConfig } from '@/types';

/**
 * Experiment 8: Preparation of an Aluminum Compound from Aluminum Cans
 * CHEM-1131 Laboratory
 *
 * Students synthesize potassium aluminum sulfate (alum) from aluminum,
 * demonstrating recycling of metals into useful compounds.
 */
const experiment8Config: ExperimentConfig = {
  id: 'exp8',
  number: 8,
  name: 'Aluminum Compound from Aluminum Cans',
  description: 'Synthesize potassium aluminum sulfate (alum) from aluminum metal through a multi-step reaction involving dissolution in base, acidification, and crystallization. Calculate theoretical and percent yield.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Understand the chemistry of aluminum dissolution in base',
    'Observe hydrogen gas evolution as a product of redox reactions',
    'Perform crystallization from supersaturated solution',
    'Determine melting point as a purity indicator',
    'Calculate theoretical and percent yield'
  ],
  safetyNotes: [
    'KOH causes chemical burns - DO NOT SPLATTER THE SOLUTION - wear gloves and goggles',
    'Work in fume hood during dissolution (hydrogen gas is flammable)',
    'H2SO4 is corrosive - add slowly with stirring',
    'Hot plates and hot solutions can cause burns',
    'Never heat before fume hood sash is lowered',
    'Disposal: Potassium hydroxide and sulphuric acid must be disposed of properly (see Appendix G)',
    'Never return unused chemicals to reagent bottles — dispose as directed by instructor'
  ],
  requiredEquipment: [
    'Top-loader balance (2 decimal places)',
    'Analytical balance',
    '250 mL beaker',
    'Hot plate',
    'Fume hood',
    'Ring stand with ring clamp',
    'Ice bath (1000 mL container, filled 2/3 with ice)',
    'Vacuum filtration setup',
    'Buchner funnel',
    'Filter flask',
    'Whatman #2 filter paper (11 cm) — weigh before use',
    'Watch glass',
    'Drying oven (set to 90°C before lab begins)',
    'Desiccator',
    'Melting point apparatus',
    'Melting point capillary tubes',
    'Mortar and pestle (for pulverizing sample)',
    'Glass stirring rod',
    'Porcelain evaporating dish (alternative drying)'
  ],
  parts: [
    {
      id: 1,
      name: 'preparation',
      title: 'Part I: Aluminum Preparation',
      equipment: ['Top-loader balance', 'Scissors', 'Aluminum foil', '250 mL beaker'],
      initialState: {
        safetyAcknowledged: false,
        aluminumMass: null,
        aluminumPrepared: false
      },
      steps: [
        {
          id: 1,
          title: 'Safety Briefing',
          shortDescription: 'Review safety procedures for handling KOH and H2SO4',
          handler: 'handleSafetyAcknowledgement',
          infoContent: {
            title: 'Recycling Aluminum',
            content: 'This experiment demonstrates how waste materials can be transformed into useful compounds. Aluminum from beverage cans will become potassium aluminum sulfate (alum), used in dyeing fabrics and making pickles.',
            whatHappens: 'Safety protocols are reviewed for handling caustic KOH and corrosive H2SO4 before synthesizing alum from aluminum metal.',
            observe: [
              'Locate gloves and goggles before handling KOH solution',
              'Verify the fume hood is operational for the dissolution step',
              'Note that KOH causes chemical burns and should not be splattered',
            ],
          }
        },
        {
          id: 2,
          title: 'Weigh Aluminum',
          shortDescription: 'Weigh approximately 1.0 g of aluminum to 2 decimal places',
          handler: 'handleWeighAluminum',
          validation: {
            condition: 'aluminumMass >= 0.9 && aluminumMass <= 1.1'
          },
          infoContent: {
            title: 'Sample Preparation',
            content: 'Cut the aluminum into very small pieces to increase surface area for faster reaction. Small pieces dissolve more uniformly.',
            whatHappens: 'Approximately 1.0 g of aluminum is weighed on a top-loader balance, then cut into small pieces to maximize surface area for the dissolution reaction.',
            observe: [
              'Record the mass to 2 decimal places on the top-loader balance',
              'Target mass is approximately 1.0 g (0.9-1.1 g acceptable)',
              'Cut into very small pieces for faster, more uniform dissolution',
            ],
          }
        },
        {
          id: 3,
          title: 'Transfer to Beaker',
          shortDescription: 'Place aluminum pieces in a 250 mL beaker',
          handler: 'handleTransferAluminum'
        }
      ]
    },
    {
      id: 2,
      name: 'dissolution',
      title: 'Part II: Aluminum Dissolution',
      equipment: ['250 mL beaker', 'Hot plate', 'Fume hood', 'KOH solution'],
      initialState: {
        fumeHoodSashLevel: 100,
        isHeating: false,
        gasEvolutionRate: 'none',
        aluminumDissolved: false,
        solutionColor: '#C0C0C0'
      },
      steps: [
        {
          id: 1,
          title: 'Lower Fume Hood Sash',
          shortDescription: 'Lower the fume hood sash to safe working level',
          handler: 'handleLowerSash',
          validation: {
            condition: 'fumeHoodSashLevel <= 30'
          },
          infoContent: {
            title: 'Fume Hood Safety',
            content: 'Always lower the sash before beginning any reaction that produces gases. The fume hood protects you from toxic or flammable fumes.',
            whatHappens: 'The fume hood sash is lowered to safe working level before the aluminum dissolution reaction, which produces flammable hydrogen gas.',
            observe: [
              'Lower the sash to the lowest safe working level',
              'Verify airflow is active by checking the indicator',
              'Never begin heating until the sash is properly positioned',
            ],
          }
        },
        {
          id: 2,
          title: 'Add Potassium Hydroxide',
          shortDescription: 'Add 50 mL of KOH solution to the aluminum',
          handler: 'handleAddKOH',
          infoContent: {
            title: 'Aluminum Dissolution',
            content: 'Aluminum rapidly dissolves in hot potassium hydroxide. The vigorous bubbling is hydrogen gas being released!',
            chemicalEquation: '2Al + 2K⁺ + 2OH⁻ + 6H₂O → 2K⁺ + 2Al(OH)₄⁻ + 3H₂',
            whatHappens: 'KOH solution is added to the aluminum pieces; the strong base dissolves the amphoteric aluminum metal, producing potassium aluminate and hydrogen gas.',
            observe: [
              'Add 50 mL of KOH solution carefully to the aluminum in the beaker',
              'Do not splatter the solution as KOH causes chemical burns',
              'The solution may start reacting even before heating begins',
            ],
          }
        },
        {
          id: 3,
          title: 'Heat and React',
          shortDescription: 'Heat gently until aluminum dissolves (~10 min)',
          handler: 'handleHeatDissolution',
          infoContent: {
            title: 'Hydrogen Gas Release',
            content: 'The bubbles are hydrogen gas (H₂). This is produced when aluminum metal is oxidized by water in the presence of a strong base. Hydrogen is flammable — keep flames away! After about **10 minutes** the aluminum should be mostly dissolved except for small impurities. **If the liquid level drops to 1/4 the original volume**, add deionized water to bring it back to **1/2 the original level**. You may need to do this several times before all chips dissolve.',
            whatHappens: 'Gentle heating accelerates the dissolution of aluminum in KOH; vigorous hydrogen gas bubbling occurs as the metal is oxidized by water in basic conditions.',
            observe: [
              'Vigorous bubbling of flammable H2 gas from the dissolving aluminum',
              'The aluminum pieces gradually shrink and disappear over ~10 minutes',
              'Add deionized water if the liquid level drops to 1/4 of the original volume',
            ],
          }
        },
        {
          id: 4,
          title: 'Monitor Reaction',
          shortDescription: 'Continue heating until bubbling stops',
          handler: 'handleMonitorReaction',
          validation: {
            requires: ['gasEvolutionRate === "none"']
          }
        }
      ]
    },
    {
      id: 3,
      name: 'purification',
      title: 'Part III: Remove Impurities',
      equipment: ['Glass stirring rod', 'Filter funnel', 'Filter paper'],
      initialState: {
        impuritiesRemoved: false,
        solutionClear: false
      },
      steps: [
        {
          id: 1,
          title: 'Remove Solid Bits',
          shortDescription: 'Use a glass stirring rod to remove any solid impurities',
          handler: 'handleRemoveSolids',
          infoContent: {
            title: 'Removing Impurities',
            content: 'Aluminum cans may contain paint, plastic coating, or other materials that do not dissolve. These must be removed before crystallization.',
            whatHappens: 'Undissolved impurities such as paint chips, plastic coating fragments, or other non-aluminum materials are removed from the solution with a stirring rod.',
            observe: [
              'Look for dark specks or floating particles in the solution',
              'Use a glass stirring rod to fish out solid impurities',
              'The solution should become relatively clear after removing solids',
            ],
          }
        },
        {
          id: 2,
          title: 'Filter if Needed',
          shortDescription: 'If suspended solids remain, filter while hot through vacuum filter using Whatman #2 (11 cm) filter paper. Retain the solution and discard the filter.',
          handler: 'handleFilterSolution'
        }
      ]
    },
    {
      id: 4,
      name: 'acidification',
      title: 'Part IV: Acidification',
      equipment: ['H2SO4 solution', 'Hot plate', 'Glass stirring rod'],
      initialState: {
        solutionCooled: false,
        acidAdded: false,
        precipitateFormed: false,
        precipitateColor: '#FFFFFF'
      },
      steps: [
        {
          id: 1,
          title: 'Cool Solution',
          shortDescription: 'Remove from heat and allow to cool, stirring occasionally. Check oven temperature (90°C).',
          handler: 'handleCoolSolution'
        },
        {
          id: 2,
          title: 'Add Sulfuric Acid',
          shortDescription: 'Slowly add 20 mL of 9 M H2SO4 with continuous stirring',
          handler: 'handleAddAcid',
          infoContent: {
            title: 'Acidification',
            content: 'Adding sulfuric acid neutralizes excess base and provides sulfate ions for alum formation. The white lumps are aluminum hydroxide precipitating.',
            chemicalEquation: '2K⁺ + 2Al(OH)₄⁻ + 4H₂SO₄ → 2K⁺ + 2Al³⁺ + 4SO₄²⁻ + 8H₂O',
            whatHappens: 'Sulfuric acid is slowly added to neutralize the excess base and provide sulfate ions; white aluminum hydroxide lumps form as an intermediate precipitate.',
            observe: [
              'Add 20 mL of 9 M H2SO4 slowly with continuous stirring',
              'White lumps of Al(OH)3 form as the solution is acidified',
              'The reaction is exothermic so the solution becomes warm',
            ],
          }
        },
        {
          id: 3,
          title: 'Observe Al(OH)3 Formation',
          shortDescription: 'Note the formation of white aluminum hydroxide lumps',
          handler: 'handleObservePrecipitate',
          infoContent: {
            title: 'Aluminum Hydroxide',
            content: 'The white lumps are aluminum hydroxide, Al(OH)₃. This is an amphoteric compound - it can react with both acids and bases.',
            whatHappens: 'White gelatinous aluminum hydroxide precipitates as an intermediate product when the pH shifts from strongly basic to near neutral.',
            observe: [
              'White lumpy precipitate of Al(OH)3 is clearly visible in the solution',
              'The precipitate is gelatinous (jelly-like) in texture',
              'This intermediate will dissolve upon further heating with excess acid',
            ],
          }
        },
        {
          id: 4,
          title: 'Heat to Dissolve',
          shortDescription: 'Heat gently with stirring for ~10 min until Al(OH)₃ dissolves',
          handler: 'handleHeatDissolve',
          infoContent: {
            title: 'Dissolving Al(OH)₃',
            content: 'Heat the reaction mixture gently for about **10 minutes** or until all of the aluminum hydroxide dissolves and the solution becomes clear. While waiting, prepare the ice bath for the next step.',
            whatHappens: 'Gentle heating dissolves the Al(OH)3 precipitate in the excess sulfuric acid, producing a clear solution containing K+, Al3+, and SO42- ions.',
            observe: [
              'The white Al(OH)3 lumps gradually dissolve with heating and stirring',
              'The solution should become clear after about 10 minutes',
              'Prepare the ice bath while waiting for dissolution to complete',
            ],
          }
        }
      ]
    },
    {
      id: 5,
      name: 'crystallization',
      title: 'Part V: Crystallization',
      equipment: ['Ice bath', '1000 mL beaker', 'Ice', 'Timer'],
      initialState: {
        iceBathPrepared: false,
        coolingTime: 0,
        crystalsFormed: false,
        crystalMass: null
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Ice Bath',
          shortDescription: 'Fill 1000 mL container 2/3 full of ice, then add water to cover ice',
          handler: 'handlePrepareIceBath'
        },
        {
          id: 2,
          title: 'Cool Solution in Ice Bath',
          shortDescription: 'Place beaker in ice bath for 20 minutes',
          handler: 'handleCoolInIceBath',
          infoContent: {
            title: 'Alum Crystallization',
            content: 'As the solution cools, alum crystals form. The cold temperature decreases solubility, causing the dissolved alum to crystallize.',
            chemicalEquation: 'K⁺ + Al³⁺ + 2SO₄²⁻ + 12H₂O → KAl(SO₄)₂·12H₂O',
            whatHappens: 'Rapid cooling in the ice bath causes the supersaturated solution to crystallize, forming white octahedral crystals of potassium aluminum sulfate dodecahydrate (alum).',
            observe: [
              'White crystals begin to form on the walls and bottom of the beaker',
              'Crystal formation increases as the solution cools over 20 minutes',
              'The crystals should appear white and may have an octahedral shape',
            ],
          }
        },
        {
          id: 3,
          title: 'Observe Crystal Formation',
          shortDescription: 'Watch for the appearance of white crystals',
          handler: 'handleObserveCrystals'
        }
      ]
    },
    {
      id: 6,
      name: 'isolation',
      title: 'Part VI: Crystal Isolation',
      equipment: ['Vacuum filtration setup', 'Buchner funnel', 'Whatman #2 filter paper (pre-weighed)', 'Ethanol/water wash (50/50, 20 mL)'],
      initialState: {
        vacuumOn: false,
        filtrationComplete: false,
        washCount: 0
      },
      steps: [
        {
          id: 1,
          title: 'Set Up Vacuum Filtration',
          shortDescription: 'Assemble Buchner funnel with pre-weighed wet filter paper',
          handler: 'handleSetupVacuum',
          infoContent: {
            title: 'Filter Paper Mass',
            content: '**Weigh the filter paper before use** and record the mass. After collecting crystals you will weigh crystals + filter paper together, then subtract the filter paper mass to get the crystal mass.',
            whatHappens: 'The vacuum filtration apparatus is assembled with a pre-weighed filter paper in the Buchner funnel to collect and separate the alum crystals from the mother liquor.',
            observe: [
              'Weigh the Whatman #2 filter paper and record its mass',
              'Wet the filter paper in the Buchner funnel and turn on the vacuum',
              'Ensure the filter paper lies flat with no gaps around the edges',
            ],
          }
        },
        {
          id: 2,
          title: 'Filter Crystals',
          shortDescription: 'Pour the crystal slurry onto the filter paper',
          handler: 'handleFilterCrystals'
        },
        {
          id: 3,
          title: 'Wash Crystals',
          shortDescription: 'Wash with 50/50 ethanol/water mixture',
          handler: 'handleWashCrystals',
          infoContent: {
            title: 'Washing Crystals',
            content: 'Washing removes impurities and mother liquor from the crystal surfaces. The ethanol helps remove water and speeds drying.',
            whatHappens: 'A 50/50 ethanol/water wash solution rinses impurities and residual mother liquor from the crystal surfaces while the vacuum pulls liquid through.',
            observe: [
              'Pour the wash solution evenly over the crystals in the Buchner funnel',
              'The filtrate should run clear through the filter flask',
              'The ethanol component helps displace water for faster drying',
            ],
          }
        },
        {
          id: 4,
          title: 'Air Dry',
          shortDescription: 'Continue vacuum to draw air through crystals',
          handler: 'handleAirDry'
        }
      ]
    },
    {
      id: 7,
      name: 'drying',
      title: 'Part VII: Drying and Weighing',
      equipment: ['Watch glass', 'Drying oven', 'Analytical balance'],
      initialState: {
        ovenTemp: 90,
        dryingTime: 0,
        finalMass: null,
        crystalColor: '#FFFFFF'
      },
      steps: [
        {
          id: 1,
          title: 'Transfer to Watch Glass',
          shortDescription: 'Transfer crystals to a tared watch glass',
          handler: 'handleTransferToDry'
        },
        {
          id: 2,
          title: 'Dry in Oven',
          shortDescription: 'Dry at 90°C for 10-15 minutes',
          handler: 'handleDryInOven',
          infoContent: {
            title: 'Drying Temperature',
            content: 'The drying oven should already be set to **90°C** (turned on before the lab begins). Dry the crystals on a watch glass for **10-15 minutes**. The alum has 12 water molecules in its crystal structure — excessive heat may drive off water of crystallization.',
            whatHappens: 'The crystals are dried at exactly 90C to remove surface moisture without driving off the 12 waters of crystallization that are part of the alum structure.',
            observe: [
              'Place the crystals on a watch glass in the oven at 90C for 10-15 minutes',
              'Do not exceed 90C or the alum will lose structural water and decompose',
              'Crystals should appear brilliant white when properly dried',
            ],
          }
        },
        {
          id: 3,
          title: 'Weigh Dried Crystals',
          shortDescription: 'Record the mass of dried alum crystals',
          handler: 'handleWeighFinal'
        }
      ]
    },
    {
      id: 8,
      name: 'analysis',
      title: 'Part VIII: Melting Point and Calculations',
      equipment: ['Melting point apparatus', 'Capillary tubes', 'Calculator'],
      initialState: {
        meltingPointRange: null,
        theoreticalYield: null,
        percentYield: null
      },
      steps: [
        {
          id: 1,
          title: 'Determine Melting Point',
          shortDescription: 'Pulverize sample, load capillary tube, and measure melting point range',
          handler: 'handleMeltingPoint',
          infoContent: {
            title: 'Purity Check',
            content: 'Pure alum melts at **92-93°C**. A broad melting range or lower temperature indicates impurities. **Pulverize** about 1 g of the sample with a clean **mortar and pestle**. Push the open end of the **capillary tube** into the powdered sample, then **invert and flick** the tube (or drop it repeatedly on the desk) until the sample depth is approximately **5 mm** in the bottom.',
            whatHappens: 'The melting point of the alum product is measured to assess purity; pure alum melts sharply at 92-93C, while impure samples show a broader range at lower temperatures.',
            observe: [
              'Pulverize the sample to a fine powder with mortar and pestle',
              'Pack approximately 5 mm of sample into the capillary tube',
              'Record both the onset and completion temperatures of melting',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate Theoretical Yield',
          shortDescription: 'Calculate expected yield from aluminum mass',
          handler: 'handleCalculateTheoretical',
          infoContent: {
            title: 'Theoretical Yield',
            content: 'Theoretical yield assumes 100% conversion. For every mole of Al (26.98 g/mol), we expect 1 mole of alum (474.39 g/mol).\n\n**Formula:** TM = (mass of Al × MW of alum) / atomic mass of Al\n\n**Example:** 1.02 g Al → TM = (1.02 × 474.39) / 26.98 = **17.93 g alum**',
            whatHappens: 'The theoretical yield is calculated assuming 100% conversion of aluminum to alum using the 1:1 mole ratio and the molar masses of Al and alum.',
            observe: [
              'Use the exact mass of aluminum you weighed at the start',
              'Apply the formula: TM = (mass Al x 474.39) / 26.98',
              'Compare theoretical yield to your actual yield for percent yield calculation',
            ],
          }
        },
        {
          id: 3,
          title: 'Calculate Percent Yield',
          shortDescription: 'Compare actual yield to theoretical yield',
          handler: 'handleCalculatePercent',
          infoContent: {
            title: 'Percent Yield',
            content: 'Percent yield = (actual mass / theoretical mass) × 100. Losses occur during transfers, filtering, and incomplete reactions. Typical yields are 60-85%.',
            whatHappens: 'The percent yield is calculated by comparing the actual crystal mass to the theoretical yield, reflecting the efficiency of the synthesis.',
            observe: [
              'Typical yields range from 60-85% for this experiment',
              'Low yields may indicate losses during filtration or incomplete crystallization',
              'High yields may suggest residual moisture in the crystals',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp8',
    title: 'Aluminum Compound Quiz',
    questions: [
      {
        id: 'exp8-q1',
        type: 'mcq' as const,
        question: 'What is the product synthesized in this experiment from aluminum metal?',
        options: [
          { id: 'a', text: 'Aluminum oxide (Al₂O₃)' },
          { id: 'b', text: 'Aluminum chloride (AlCl₃)' },
          { id: 'c', text: 'Potassium aluminum sulfate, KAl(SO₄)₂·12H₂O (alum)' },
          { id: 'd', text: 'Aluminum hydroxide, Al(OH)₃' },
        ],
        correctOptionId: 'c',
        explanation: 'The experiment synthesizes potassium aluminum sulfate dodecahydrate (alum), KAl(SO₄)₂·12H₂O, by dissolving aluminum in KOH, then acidifying with H₂SO₄ and crystallizing.',
      },
      {
        id: 'exp8-q2',
        type: 'mcq' as const,
        question: 'When aluminum is dissolved in KOH solution, what gas is evolved?',
        options: [
          { id: 'a', text: 'Oxygen (O₂)' },
          { id: 'b', text: 'Hydrogen (H₂)' },
          { id: 'c', text: 'Carbon dioxide (CO₂)' },
          { id: 'd', text: 'Chlorine (Cl₂)' },
        ],
        correctOptionId: 'b',
        explanation: 'Aluminum reacts with KOH solution in a redox reaction: 2Al + 2KOH + 6H₂O → 2KAl(OH)₄ + 3H₂\u2191. The hydrogen gas is flammable, which is why this reaction must be performed in a fume hood.',
      },
      {
        id: 'exp8-q3',
        type: 'mcq' as const,
        question: 'How is the purity of the synthesized alum crystals assessed?',
        options: [
          { id: 'a', text: 'By measuring the pH of a dissolved sample' },
          { id: 'b', text: 'By determining the melting point' },
          { id: 'c', text: 'By measuring the volume of crystals' },
          { id: 'd', text: 'By testing solubility in alcohol' },
        ],
        correctOptionId: 'b',
        explanation: 'Melting point determination is a standard purity test for crystalline compounds. Pure alum melts at 92.5°C. Impurities lower and broaden the melting point range, so a sharp melting point close to the literature value indicates high purity.',
      },
      {
        id: 'exp8-q4',
        type: 'short_answer' as const,
        question: 'Explain why ice-bath cooling is necessary during crystallization of alum. What would happen if the solution were cooled too slowly?',
        sampleAnswer: 'Rapid cooling in an ice bath causes the alum solution to become supersaturated quickly, which promotes the formation of many small crystal nuclei and produces a higher yield of crystals. Slow cooling allows fewer, larger crystals to form and may result in lower yield because the solution may not become supersaturated enough. Rapid cooling also reduces the time available for impurities to incorporate into the crystal lattice.',
      },
      {
        id: 'exp8-q5',
        type: 'calculation' as const,
        question: 'If you start with 1.00 g of aluminum (molar mass = 26.98 g/mol) and the theoretical yield of alum KAl(SO₄)₂·12H₂O (molar mass = 474.39 g/mol) is one mole of alum per mole of Al, what is the theoretical yield of alum in grams?',
        expectedValue: 17.58,
        tolerance: 2,
        unit: 'g',
      },
    ],
  },
};

// Reaction color progression data
export const REACTION_COLORS = {
  initialAlKOH: { color: '#C0C0C0', description: 'Metallic aluminum in clear solution' },
  duringDissolution: { color: '#E8E8E8', description: 'Gas bubbles, aluminum dissolving' },
  afterDissolution: { color: '#F5F5F5', description: 'Clear colorless K+ and Al(OH)4- solution' },
  afterAcidAddition: { color: '#FFFFFF', description: 'Al(OH)3 lumps form (white precipitate)' },
  duringHeating: { color: '#F8F8F8', description: 'Al(OH)3 dissolves, clear solution' },
  inIceBath: { color: '#FAFAFA', description: 'Supersaturated solution' },
  crystalFormation: { color: '#FFFFFF', description: 'White alum crystals form' }
};

// Gas evolution rates
export const GAS_EVOLUTION_RATES = {
  none: { bubblesPerSec: 0, description: 'No bubbles' },
  slow: { bubblesPerSec: 3, description: 'Slow bubbling' },
  moderate: { bubblesPerSec: 8, description: 'Moderate bubbling' },
  vigorous: { bubblesPerSec: 15, description: 'Vigorous bubbling' }
};

// Crystal properties
export const CRYSTAL_DATA = {
  wetCrystals: { color: '#F0F0F0', description: 'Slightly gray' },
  dryCrystals: { color: '#FFFAFA', description: 'Brilliant white' },
  shape: 'octahedral',
  meltingPoint: 92.5,
  molarMass: 474.39
};

export default experiment8Config;
